import * as React from "react";
import { twMerge } from "tailwind-merge";
import { tv, VariantProps } from "tailwind-variants";
import { field } from "../styles";

interface InputProps extends Omit<React.ComponentPropsWithRef<"input">, "prefix">, VariantProps<typeof inputVariants> {
  prefix?: React.ReactElement;
  prefixStyles?: boolean;
  suffix?: React.ReactElement;
  suffixStyles?: boolean;
}
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, theme, disabled, readOnly, prefix, prefixStyles, suffix, suffixStyles, ...rest }, ref) => {
    const invalid = rest["aria-invalid"] === true || rest["aria-invalid"] === "true";
    const base = inputVariants({ theme, className });

    return (
      <div
        className={twMerge(
          base,
          disabled && "opacity-50 inset-shadow-none",
          invalid && "border-(--field-border-error) ring-2 ring-(--field-ring-error)",
          readOnly && "border-(--field-border-disabled) bg-(--field-bg-focus) inset-shadow-none",
        )}
      >
        {prefix && (
          <span
            className={prefixSuffixStyles({
              styles: prefixStyles,
              className: prefixStyles ? ["border-r", invalid ? "border-(--color-ds-surface-error)" : ""] : "-mr-3",
            })}
          >
            {prefix}
          </span>
        )}
        <input
          className={twMerge(
            "placeholder:text-(--field-text-placeholder)",
            "read-only:text-ds-text-tertiary",
            "disabled:cursor-not-allowed disabled:text-(--field-text-disabled)",
            "file:text-ds-text-inverse file:bg-ds-neutral-12 file:mr-2 file:h-6 file:rounded-sm file:border-0 file:px-2 file:text-xs file:font-medium",
            "block w-full flex-1 bg-transparent px-3 py-1 text-(--field-text) outline-none",
          )}
          ref={ref}
          disabled={disabled}
          readOnly={readOnly}
          {...rest}
        />
        {suffix && (
          <span
            className={prefixSuffixStyles({
              styles: suffixStyles,
              className: suffixStyles ? ["border-l", invalid ? "border-(--color-ds-surface-error)" : ""] : "-ml-3",
            })}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

const prefixSuffixStyles = tv({
  base: "h-full px-3 text-sm flex items-center text-ds-text-placeholder bg-transparent",
  variants: {
    styles: {
      true: "border-ds-stroke-primary bg-ds-surface-foundation",
    },
  },
});

export const inputVariants = tv({
  extend: field,
  base: [
    // Base Styles
    "flex h-10 text-sm items-center overflow-clip rounded-md",
    "bg-(--field-bg) border border-(--field-border)",
    "transition-all inset-shadow-sm",

    // Focus Styles
    "focus-within:bg-(--field-bg-focus)",
    "focus-within:border-(--field-border-focus)",
    "focus-within:ring-2 focus-within:ring-(--field-ring)",
    "focus-within:inset-shadow-none",
  ],
  variants: {
    theme: {
      solid: "",
      outline: "[--field-bg:var(--color-ds-surface-foundation)]",
    },
  },
  defaultVariants: {
    theme: "solid",
  },
});
